import {
  Container,
  Stack,
  Title,
  Text,
  Card,
  Badge,
  Paper,
  List,
  Center,
  Loader,
  Alert,
} from "@mantine/core";
import { PieChart } from "@mantine/charts";
import { useParams } from "react-router-dom";
import { useGetPublicPollResults } from "../api/polls";
import { IconAlertCircle } from "@tabler/icons-react";

export default function PublicPollResults() {
  const { shareToken } = useParams();
  const { data, isLoading, isError } = useGetPublicPollResults(shareToken);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (isError || !data) {
    return (
      <Container size="md" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Poll results not found or the link has expired.
        </Alert>
      </Container>
    );
  }

  const colors = [
    "#4caf50",
    "#2196f3",
    "#ff9800",
    "#e91e63",
    "#9c27b0",
    "#00bcd4",
    "#8bc34a",
    "#ffc107",
    "#ff5722",
    "#673ab7",
  ];

  const { poll, results, totalVotes } = data;

  const getChartData = () => {
    if (poll.type === "text") {
      return null;
    }

    return poll.options.map((option, index) => ({
      name: option.title,
      value: results[option.id]?.count || 0,
      color: colors[index % colors.length],
    }));
  };

  const chartData = getChartData();
  const hasVotes = chartData && chartData.some((item) => item.value > 0);

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        <Stack gap="xs">
          <Title order={1}>{poll.title}</Title>
          {poll.description && (
            <Text size="lg" c="dimmed">
              {poll.description}
            </Text>
          )}
          <Badge size="lg" variant="light" color="blue">
            Total Votes: {totalVotes}
          </Badge>
        </Stack>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            {poll.type === "text" ? (
              <>
                <Title order={3}>Responses</Title>
                <List spacing="sm">
                  {results.map((response, index) => (
                    <List.Item key={index}>
                      <Paper p="md" withBorder>
                        {response.answer.map((ans, idx) => (
                          <Text key={idx}>{ans.text}</Text>
                        ))}
                      </Paper>
                    </List.Item>
                  ))}
                </List>
              </>
            ) : (
              <>
                {hasVotes ? (
                  <>
                    <Title order={3} mb="md">
                      Results Chart
                    </Title>
                    <div>
                      <PieChart
                        h={300}
                        data={chartData}
                        radius={0.8}
                        labelsPosition="outside"
                        labelsType="info"
                        withLabels
                        withTooltip
                      />
                    </div>
                  </>
                ) : (
                  <Alert icon={<IconAlertCircle size={16} />} color="blue">
                    No votes yet. Charts will appear once voting begins.
                  </Alert>
                )}

                <Stack gap="xs">
                  <Title order={3}>Detailed Results</Title>
                  {poll.options.map((option) => {
                    const count = results[option.id]?.count || 0;
                    const percentage =
                      totalVotes > 0
                        ? ((count / totalVotes) * 100).toFixed(1)
                        : 0;

                    return (
                      <Paper key={option.id} p="md" withBorder>
                        <Stack gap="xs">
                          <Text fw={500}>{option.title}</Text>
                          <Text size="sm" c="dimmed">
                            {count} votes ({percentage}%)
                          </Text>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </>
            )}
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
